import {
  INQUIRY_CONSTANTS,
} from '@/constants';

import {
  customer_inquiry_repository,
  search_repository,
} from '@/repositories';

const state = () => ({
  customer_inquiry_count: 0,
  customer_inquiry_list: [],
  inquiry_type_id_list: [],
  page: 1,
  quick_search: '',
  status_id_list: [],
  user_has_never_submitted_any_inquiry: false,
  inquiry_quota_count: 0,
  user_has_reached_inquiry_quota: false,
});

const getters = {
  customerInquiryCount: (state) => state.customer_inquiry_count,
  customerInquiryList: (state) => state.customer_inquiry_list,
  inquiryTypeIdList: (state) => state.inquiry_type_id_list,
  page: (state) => state.page,
  quickSearch: (state) => state.quick_search,
  statusIdList: (state) => state.status_id_list,
  userHasNeverSubmittedAnyInquiry: (state) => state.user_has_never_submitted_any_inquiry,
  userHasReachedInquiryQuota: (state) => state.user_has_reached_inquiry_quota,
  inquiryQuotaCount: (state) => state.inquiry_quota_count,
};

const actions = {
  async checkIfUserHasReachedInquiryQuota({ commit, rootState }, { user_id }) {
    const criteria = {
      user_id_list: [user_id],
      status_id_list: [
        INQUIRY_CONSTANTS.STATUS_ID.TODO,
        INQUIRY_CONSTANTS.STATUS_ID.DRAFT,
        INQUIRY_CONSTANTS.STATUS_ID.IN_PROGRESS,
      ],
      limit: 0,
    };

    try {
      const {
        customer_inquiry_count,
      } = await customer_inquiry_repository.searchCustomersInquiry(criteria);

      const {
        send_inquiry_limit,
      } = rootState.user.user_data;

      const inquiry_max_quota = send_inquiry_limit
        ?? INQUIRY_CONSTANTS.INQUIRY_MAX_QUOTA;

      commit('set_inquiry_quota_count', customer_inquiry_count);
      commit('set_user_has_reached_inquiry_quota', customer_inquiry_count >= inquiry_max_quota);
    } catch (error) {
      console.error(error);
    }
  },
  async createCustomersInquiry(_, data) {
    return customer_inquiry_repository.createCustomersInquiry(data);
  },
  async searchCustomersInquiry({ commit, state }, { user_id }) {
    const criteria = {
      limit: INQUIRY_CONSTANTS.INQUIRY_PER_PAGE_COUNT,
      offset: (state.page * INQUIRY_CONSTANTS.INQUIRY_PER_PAGE_COUNT) - INQUIRY_CONSTANTS
        .INQUIRY_PER_PAGE_COUNT,
      sort_list: ['-creation_date'],
      user_id_list: [user_id],
    };

    if (state.inquiry_type_id_list.length > 0) {
      criteria.inquiry_type_id_list = state.inquiry_type_id_list;
    }

    if (state.quick_search.length > 0) {
      criteria.quick_search = state.quick_search;
    }

    if (state.status_id_list.length > 0) {
      criteria.status_id_list = [];

      if (state.status_id_list.includes(INQUIRY_CONSTANTS.STATUS_ID.TODO)) {
        criteria.status_id_list.push(INQUIRY_CONSTANTS.STATUS_ID.TODO);
        criteria.status_id_list.push(INQUIRY_CONSTANTS.STATUS_ID.DRAFT);
      }

      if (state.status_id_list.includes(INQUIRY_CONSTANTS.STATUS_ID.IN_PROGRESS)) {
        criteria.status_id_list.push(INQUIRY_CONSTANTS.STATUS_ID.IN_PROGRESS);
        criteria.status_id_list.push(INQUIRY_CONSTANTS.STATUS_ID.PRE_DONE);
      }

      if (state.status_id_list.includes(INQUIRY_CONSTANTS.STATUS_ID.DONE)) {
        criteria.status_id_list.push(INQUIRY_CONSTANTS.STATUS_ID.DONE);
      }

      if (state.status_id_list.includes(INQUIRY_CONSTANTS.STATUS_ID.PARTIALLY_DONE)) {
        criteria.status_id_list.push(INQUIRY_CONSTANTS.STATUS_ID.PARTIALLY_DONE);
      }

      if (state.status_id_list.includes(INQUIRY_CONSTANTS.STATUS_ID.FAIL)) {
        criteria.status_id_list.push(INQUIRY_CONSTANTS.STATUS_ID.FAIL);
      }

      if (state.status_id_list.includes(INQUIRY_CONSTANTS.STATUS_ID.EDITORIAL_OFFLINE)) {
        criteria.status_id_list.push(INQUIRY_CONSTANTS.STATUS_ID.EDITORIAL_OFFLINE);
      }
    }

    const response = await customer_inquiry_repository.searchCustomersInquiry(criteria);

    if (response.customer_inquiry_count === 0) {
      commit('set_customer_inquiry_count', response.customer_inquiry_count);
      commit('set_customer_inquiry_list', response.customer_inquiry_list);
      commit('set_page', 1);

      if (state.status_id_list.length === 0) {
        commit('set_user_has_never_submitted_any_inquiry', true);
      }

      return response;
    }

    const company_id_set = new Set();

    response.customer_inquiry_list.forEach((customer_inquiry) => {
      if (Number.isInteger(customer_inquiry.company_id)) {
        company_id_set.add(customer_inquiry.company_id);
      }

      if (Number.isInteger(customer_inquiry.source_company_id)) {
        company_id_set.add(customer_inquiry.source_company_id);
      }
    });

    const { account_list } = await search_repository.getAccountsAll({
      company_id_list: Array.from(company_id_set)
        .filter((company_id) => company_id !== null),
    });

    const { contact_list } = await search_repository.getContactsAll({
      contact_id_list: response.customer_inquiry_list
        .map((customer_inquiry) => customer_inquiry.contact_id)
        .filter((contact_id) => contact_id !== null),
    });

    const getCompanyData = (customer_inquiry) => {
      const {
        company_id,
        source_company_id,
      } = customer_inquiry;

      /**
       * This code is not clean but to adapt to MOA's request
       * who want to see origin company and main job at the moment of the inquiry creation
       * try to source company first, then company
       * https://polarity.assembla.com/spaces/new-desk/tickets/realtime_cardwall?ticket=7344
       */
      const company = account_list.find(
        (company) => company.id === source_company_id || company.id === company_id
      );

      return (company === undefined)
        ? null
        : {
          description: (company.industry_type_list === null)
            ? ''
            : company.industry_type_list
              .find((industry_type) => industry_type.position === 1)
              .name,
          id: company.id,
          name: company.name,
        };
    };

    const getContactData = (customer_inquiry) => {
      const {
        contact_id,
        job_id,
        job_name,
      } = customer_inquiry;

      const contact = contact_list.find((contact) => contact.id === contact_id);

      if (contact_id == null) {
        return null;
      }

      let job_title = null;

      if (job_name !== null) {
        job_title = job_name;
      } else if (job_id !== null) {
        job_title = contact?.job_list
          ?.find((job) => job.id === job_id)
          ?.name || null;
      }

      if (job_title === null) {
        job_title = contact?.job_list
          ?.find((job) => job.main && job.active)
          ?.name || null;
      }

      return (contact === undefined)
        ? INQUIRY_CONSTANTS.OPTED_OUT
        : {
          first_name: contact.first_name,
          id: contact_id,
          civility_id: contact.civility.id,
          job_title,
          last_name: contact.last_name,
        };
    };

    response.customer_inquiry_list = response.customer_inquiry_list.map((customer_inquiry) => ({
      ...customer_inquiry,
      data: {
        company: getCompanyData(customer_inquiry),
        contact: getContactData(customer_inquiry),
        inquiry_type: {
          id: customer_inquiry.inquiry_type_id,
          sub_type_id: customer_inquiry.inquiry_sub_type_id,
        },
      },
    }));

    commit('set_customer_inquiry_count', response.customer_inquiry_count);
    commit('set_customer_inquiry_list', response.customer_inquiry_list);
    commit('set_page', 1);

    return response;
  },

  async searchAccountListByCompanyWebsiteUrlList(_, company_website_url_list) {
    const { account_list } = await search_repository.searchAccountActive({
      company_website_url_list,
    });

    return account_list;
  },

  async getMobilePhone(_, job_id) {
    const { mobile_phone } = await customer_inquiry_repository.getMobilePhone(job_id);

    return mobile_phone;
  },

  async acceptMobilePhoneTermsOfUse() {
    const result = await customer_inquiry_repository.acceptMobilePhoneTermsOfUse();

    return result;
  },
};

const mutations = {
  set_customer_inquiry_count(state, customer_inquiry_count) {
    state.customer_inquiry_count = customer_inquiry_count;
  },
  set_customer_inquiry_list(state, customer_inquiry_list) {
    state.customer_inquiry_list = customer_inquiry_list;
  },
  set_inquiry_type_id_list(state, inquiry_type_id_list) {
    state.inquiry_type_id_list = inquiry_type_id_list;
  },
  set_page(state, page) {
    state.page = page;
  },
  set_quick_search(state, quick_search) {
    state.quick_search = quick_search;
  },
  set_status_id_list(state, status_id_list) {
    state.status_id_list = status_id_list;
  },
  set_user_has_never_submitted_any_inquiry(state, boolean) {
    state.user_has_never_submitted_any_inquiry = boolean;
  },
  set_inquiry_quota_count(state, inquiry_quota_count) {
    state.inquiry_quota_count = inquiry_quota_count;
  },
  set_user_has_reached_inquiry_quota(state, user_has_reached_inquiry_quota) {
    state.user_has_reached_inquiry_quota = user_has_reached_inquiry_quota;
  },
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state,
};
